import moment from 'moment';

/** Option 1 (default): calendar month shift. Option 2: trailing / sequential block. */
export const COMPARISON_MODES = {
  SEQUENTIAL: 'sequential',
  EQUIVALENT: 'equivalent',
};

const PERIOD_CONTEXT = {
  YESTERDAY: 'yesterday',
  LAST_30: 'last30',
  THIS_MONTH: 'thisMonth',
  CUSTOM: 'custom',
};

const MODE_LABELS = {
  [PERIOD_CONTEXT.YESTERDAY]: {
    [COMPARISON_MODES.SEQUENTIAL]: 'Previous day',
    [COMPARISON_MODES.EQUIVALENT]: 'Same day last month',
  },
  [PERIOD_CONTEXT.LAST_30]: {
    [COMPARISON_MODES.SEQUENTIAL]: 'Previous 30 days',
    [COMPARISON_MODES.EQUIVALENT]: 'Same dates last month',
  },
  [PERIOD_CONTEXT.THIS_MONTH]: {
    [COMPARISON_MODES.SEQUENTIAL]: 'Previous period',
    [COMPARISON_MODES.EQUIVALENT]: 'Previous month',
  },
  [PERIOD_CONTEXT.CUSTOM]: {
    [COMPARISON_MODES.SEQUENTIAL]: 'Previous period',
    [COMPARISON_MODES.EQUIVALENT]: 'Previous month',
  },
};

export function getPeriodComparisonContext(period) {
  const label = period?.label;

  if (label === 'Yesterday') return PERIOD_CONTEXT.YESTERDAY;
  if (label === 'Last 30 days') return PERIOD_CONTEXT.LAST_30;
  if (label === 'This month') return PERIOD_CONTEXT.THIS_MONTH;

  return PERIOD_CONTEXT.CUSTOM;
}

export function normalizeComparisonMode(mode) {
  if (mode === 'trailing' || mode === COMPARISON_MODES.SEQUENTIAL) {
    return COMPARISON_MODES.SEQUENTIAL;
  }
  if (mode === 'mtd' || mode === COMPARISON_MODES.EQUIVALENT) {
    return COMPARISON_MODES.EQUIVALENT;
  }
  return COMPARISON_MODES.EQUIVALENT;
}

export function getComparisonModeOptions(period) {
  const context = getPeriodComparisonContext(period);
  const labels = MODE_LABELS[context];

  return [
    {value: COMPARISON_MODES.EQUIVALENT, label: labels[COMPARISON_MODES.EQUIVALENT]},
    {value: COMPARISON_MODES.SEQUENTIAL, label: labels[COMPARISON_MODES.SEQUENTIAL]},
  ];
}

export function getComparisonModeLabel(period, mode) {
  const normalized = normalizeComparisonMode(mode);
  const option = getComparisonModeOptions(period).find(({value}) => value === normalized);
  return option?.label || MODE_LABELS[PERIOD_CONTEXT.CUSTOM][COMPARISON_MODES.EQUIVALENT];
}

function buildEquivalentComparisonPeriod(start, end) {
  if (start.isSame(end, 'day')) {
    const previous = start.clone().subtract(1, 'month');
    return {
      startDate: previous.format('YYYY-MM-DD'),
      endDate: previous.format('YYYY-MM-DD'),
    };
  }

  return {
    startDate: start.clone().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: end.clone().subtract(1, 'month').format('YYYY-MM-DD'),
  };
}

function buildSequentialComparisonPeriod(start, end) {
  let spanDays = end.diff(start, 'days');
  spanDays = spanDays === 0 ? 1 : spanDays + 1;

  return {
    startDate: start.clone().subtract(spanDays, 'days').format('YYYY-MM-DD'),
    endDate: end.clone().subtract(spanDays, 'days').format('YYYY-MM-DD'),
  };
}

export function buildComparisonPeriod(
  startDate,
  endDate,
  mode = COMPARISON_MODES.EQUIVALENT
) {
  const start = moment(startDate);
  const end = moment(endDate);
  const normalized = normalizeComparisonMode(mode);

  if (normalized === COMPARISON_MODES.SEQUENTIAL) {
    return buildSequentialComparisonPeriod(start, end);
  }

  return buildEquivalentComparisonPeriod(start, end);
}

export function formatDateRange(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  const sameYear = start.year() === end.year();
  const fmt = date => date.format(sameYear ? 'MMM D' : 'MMM D, YYYY');

  if (start.isSame(end, 'day')) return fmt(start);
  return `${fmt(start)}–${fmt(end)}`;
}

export function getComparisonPeriodLabel(comparisonPeriod) {
  if (!comparisonPeriod?.startDate || !comparisonPeriod?.endDate) return '';
  return formatDateRange(comparisonPeriod.startDate, comparisonPeriod.endDate);
}

export function getComparisonDateContext(period, mode, {showCurrentPeriod = false} = {}) {
  if (!period?.startDate || !period?.endDate) return '';

  const comparison = buildComparisonPeriod(period.startDate, period.endDate, mode);
  const previous = formatDateRange(comparison.startDate, comparison.endDate);

  if (!showCurrentPeriod) return previous;

  const current = formatDateRange(period.startDate, period.endDate);
  return `${current} vs. ${previous}`;
}

export function getComparisonTooltip(period, comparisonPeriod, mode) {
  const label = period ? getComparisonModeLabel(period, mode) : '';
  const context = period
    ? getComparisonDateContext(period, mode)
    : getComparisonPeriodLabel(comparisonPeriod);

  if (label && context) return `${label} · ${context}`;
  return label || context || '';
}
